import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {formatDate, formatNumber, formatPercent} from '@angular/common';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {of, Subscription, TimeoutError} from 'rxjs';
import {catchError, filter, timeout} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {
  AdminDashboardCompanyCommercial,
  AdminDashboardCompanyInsight,
  AdminDashboardCompanyLicence,
  AdminDashboardFlagAction,
  AdminDashboardInsight,
  AdminDashboardInsightFlag,
  AdminDashboardPoint
} from '@features/admin-panel/models/admin-dashboard.models';
import {adbAlpha, adbCategoryAxis, AdbChartTheme, adbChartTheme, adbGrid, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbUsdCompact, adbUsdFull} from '@features/admin-panel/helpers/admin-dashboard-format';
import {adbDrillMeta} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {hasFeature} from '@shared/store/selectors/security.selector';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const whyPanelCharts = {
  init: (el: HTMLElement): echarts.ECharts => echarts.init(el)
};

/** How long Snooze / Mark handled wait for the server before giving up (ITP-E-143-07). */
export const FLAG_ACTION_TIMEOUT_MS = 30000;
/** Shown when the flag action did not answer in time; the command may still complete on the server. */
export const FLAG_ACTION_TIMEOUT_MESSAGE = 'No answer from the server yet. Your change may still be saved: reopen the panel in a minute to check.';
/** The tier names of the verdict badge (OCA-E-159 attention.ts TIER_LABEL); the server's tiers are InsightRules.Tier. */
export const WHY_TIER_LABEL: Record<string, string> = {critical: 'Critical', warning: 'Warning', watch: 'Watch', healthy: 'Healthy'};
/** The icon of each flag kind in its chip (OCA-E-159 WhyPanel.tsx:27; usage ITP-E-216-07). */
export const WHY_FLAG_ICON: Record<string, string> = {renewal: 'fa-file-contract', dark: 'fa-moon', usage: 'fa-arrow-trend-down', spike: 'fa-chart-line-up'};

/**
 * The tier band of a 0-100 score, as the server bands the health score (InsightRules.cs:104) and the mockup colours
 * the health-part bars (WhyPanel.tsx:219): below 40 critical, below 65 warning, below 80 watch, else healthy.
 */
export function whyScoreTier(score: number): string {
  return score < 40 ? 'critical' : score < 65 ? 'warning' : score < 80 ? 'watch' : 'healthy';
}

/** The server's flag names (InsightRules RenewalRisk / GoingDark / UsageFalling / SpendSpike), for a history row whose flag is no longer open. */
export const WHY_FLAG_LABEL: Record<string, string> = {renewal: 'Renewal risk', dark: 'Going dark', usage: 'Usage falling', spike: 'Spend spike'};

/**
 * The evidence behind a company's flags: tier and score, each flag's reason and factors, a 16-week logins bar chart,
 * contract end / seats / XatBot quality and the health parts. Loads the company's insight whenever `companyId`
 * changes. The chart exists only while there is an insight to draw.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-why-panel',
  templateUrl: './admin-dashboard-why-panel.component.html',
  styleUrls: ['./admin-dashboard-why-panel.component.scss']
})
export class AdminDashboardWhyPanelComponent implements OnChanges, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() companyId: string | null;
  @Output() closed = new EventEmitter<void>();
  @Output() changed = new EventEmitter<void>();

  data: AdminDashboardCompanyInsight | null = null;
  notes: Record<string, string> = {};
  busy = false;
  /** The flag whose Snooze / Mark handled compose box is open (OCA-E-159 WhyPanel.tsx:151-175), or null. */
  composing: {kind: string; action: 'snooze' | 'handled'} | null = null;
  error: string | null = null;
  /** The flag kind whose Reopen ran last, so its error shows in that flag's own state box (ITP-E-164-03). */
  reopening: string | null = null;
  /** The company's live Client Manager licences (Active or Upcoming) for the Contract block; null until they load (ITP-E-165-07). */
  licences: AdminDashboardCompanyLicence[] | null = null;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;
  private commercialSubscription: Subscription;
  private featureSubscription: Subscription;

  granted: (feature: string) => boolean = () => false;

  constructor(private dataService: AdminDashboardDataService, private router: Router, private store: Store) {
    // No take(1): hasFeature emits a function before the user's features have loaded, so every later emission
    // re-grants. Unsubscribed in ngOnDestroy.
    this.featureSubscription = this.store.pipe(select(hasFeature), filter(fn => !!fn))
      .subscribe(fn => this.granted = f => fn(f) === true);
  }

  get insight(): AdminDashboardInsight | null {
    return this.data?.Insight ?? null;
  }

  /** The tier that colours the verdict: the insight's tier, or 'none' when the company has no score yet. */
  get tierKey(): string {
    return this.insight?.tier || 'none';
  }

  /** The verdict badge's text ("Warning"), or null when the tier is unknown. */
  get tierLabel(): string | null {
    const tier = this.insight?.tier;
    return tier ? WHY_TIER_LABEL[tier] ?? null : null;
  }

  /**
   * The line under the tier badge (OCA-E-159 WhyPanel.tsx:105-109 with Plan ITP-P-35 Q2: owner and seats instead of
   * country and size): "acme · Jane Doe · 12 seats", "acme · Unassigned · 4 seats", "acme · Not in Client Manager".
   * Without `Meta` (an older server) it stays "acme · Client" / "acme · Non-client" (ITP-E-165-06).
   */
  get metaLine(): string {
    const id = this.insight?.companyId ?? this.companyId;
    const meta = this.data?.Meta;
    if (!meta) {
      return `${id} · ${this.insight?.isClient ? 'Client' : 'Non-client'}`;
    }
    return `${id} · ${adbDrillMeta({
      companyId: meta.companyId,
      companyName: meta.companyName,
      ownerName: meta.isClient ? meta.ownerName : null,
      seats: meta.seats
    })}`;
  }

  /**
   * The line under "Why it's flagged" for a company with no open flag (Carbon WhyPanel.tsx "Not flagged"): its
   * 4-week against 12-week login ratio and, when known, the contract end.
   */
  get notFlaggedText(): string {
    const signals = this.insight?.signals;
    const ratio = signals?.engagementRatio;
    const logins = ratio !== null && ratio !== undefined
      ? `Logins over 4 weeks are at ${formatPercent(ratio, 'en-US', '1.0-0')} of the 12-week average`
      : 'No recent logins';
    const contract = signals?.nextRenewal
      ? `, and the contract runs to ${formatDate(signals.nextRenewal, 'd MMM y', 'en-US', 'UTC')}.`
      : '.';
    return `Not flagged. ${logins}${contract}`;
  }

  /** True when at least one "Open in" link is granted (each link keeps its own feature check). */
  get hasLinks(): boolean {
    // GEN-E-182-26: the EAT usage link follows the Usage Report app's feature, APUsageReport.
    return this.granted('APManagement') || (this.granted('APClientManager') && !!this.insight?.isClient)
      || this.granted('APOnlineUser') || this.granted('APUsageReport');
  }

  /** The company's average weekly logins over 12 weeks (`signals.weekly12` is already an average per week). */
  get loginsAverage(): number {
    return this.insight?.signals?.weekly12 ?? 0;
  }

  /** The line under "Logins, last 16 weeks" (OCA-E-159 WhyPanel.tsx:229-231). */
  get loginsNote(): string {
    const average = this.loginsAverage;
    return average >= 1
      ? `12-week average ${formatNumber(average, 'en-US', '1.0-0')} a week; the last four weeks are drawn strong.`
      : 'Too few logins for a trend.';
  }

  /** "15 Nov 2026 · in 43 days", counted from the insight's day; "No active contract" without a renewal date. */
  get contractEndsText(): string {
    const end = this.insight?.signals?.nextRenewal;
    if (!end) {
      return 'No active contract';
    }
    const days = Math.round((Date.parse(end) - Date.parse(this.insight.date)) / 86400000);
    return `${formatDate(end, 'd MMM y', 'en-US', 'UTC')} · in ${countOf(days, 'day')}`;
  }

  /** The client's ARR in full dollars (OCA-E-159 fmtUsdFull): "$12,000". */
  get arrText(): string {
    return adbUsdFull(this.insight?.arr ?? 0);
  }

  /** The Contract block shows its Licences row only to a viewer holding the Client Manager feature (ITP-E-165-07). */
  get showLicences(): boolean {
    return this.granted('APClientManager');
  }

  /**
   * One line per live licence (OCA-E-159 WhyPanel.tsx:246-256 without the per-licence seats, which the platform does
   * not hold): "Global Minimum Tax · $38.4k". The package name falls back to the product code, then to "Licence".
   */
  get licenceLines(): string[] {
    return (this.licences ?? []).map(l => `${l.packageName || l.productCode || 'Licence'} · ${adbUsdCompact(l.totalLicensePrice)}`);
  }

  /** False only while a client's licences are still loading, so the row never shows "—" before they arrive. */
  get licencesReady(): boolean {
    return !this.insight?.isClient || this.licences !== null;
  }

  /** Seats used against licensed seats (1.2 = more active users than seats); null without licensed seats. */
  get seatsRatio(): number | null {
    const s = this.insight?.signals;
    return s && s.seats > 0 ? s.seatsUsed / s.seats : null;
  }

  get seatsCaption(): string {
    const s = this.insight?.signals;
    return s && s.seats > 0 ? `of ${countOf(s.seats, 'seat')} used in 30 days` : 'active in 30 days · no licensed seats';
  }

  get seatsFillPercent(): number {
    return Math.min(100, (this.seatsRatio ?? 0) * 100);
  }

  /** More active users than seats is healthy; otherwise the band of the "seats" health part (its score). */
  get seatsTier(): string {
    const ratio = this.seatsRatio ?? 0;
    if (ratio > 1) {
      return 'healthy';
    }
    const part = this.insight?.healthParts?.find(p => p.key === 'seats');
    return whyScoreTier(part ? part.score : ratio * 100);
  }

  /** The sentence under the seat bar (OCA-E-159 WhyPanel.tsx:269-275). */
  get seatsNote(): string {
    const ratio = this.seatsRatio;
    if (ratio === null) {
      return 'No licensed seats on record.';
    }
    if (ratio > 1) {
      return 'More active users than seats — an upsell conversation.';
    }
    return `${formatPercent(ratio, 'en-US', '1.0-0')} of seats in use.`;
  }

  /**
   * The company's XatBot spend over the last 30 snapshot days in full dollars (OCA-E-159 WhyPanel.tsx:287-288
   * fmtUsdFull): "$1,235"; null when the server sends none, or without APUsageReport (GEN-E-182-26: the Usage Report
   * app's feature, the gate of every other XatBot spend view), which hides the Spend row (ITP-E-165-09).
   */
  get spendText(): string | null {
    if (!this.granted('APUsageReport')) {
      return null;
    }
    const spend = this.data?.Spend30;
    return spend === null || spend === undefined ? null : adbUsdFull(spend);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && this.companyId) {
      this.composing = null;
      this.reopening = null;
      this.reload();
    }
  }

  go(url: string): void {
    this.router.navigateByUrl(url);
  }

  isSuppressed(kind: string): boolean {
    return (this.data?.Suppressed ?? []).includes(kind);
  }

  latestAction(kind: string): AdminDashboardFlagAction | null {
    return (this.data?.Actions ?? []).find(a => a.kind === kind) ?? null;   // Actions are newest-first
  }

  /** Opens the compose box for one flag; nothing is saved until it is confirmed. */
  compose(kind: string, action: 'snooze' | 'handled'): void {
    this.composing = {kind, action};
    this.reopening = null;
    this.error = null;
  }

  cancelCompose(): void {
    this.composing = null;
    this.error = null;
  }

  /** The open compose box when it belongs to this flag, else null. */
  composingFor(kind: string): {kind: string; action: 'snooze' | 'handled'} | null {
    return this.composing?.kind === kind ? this.composing : null;
  }

  isComposing(kind: string, action: 'snooze' | 'handled'): boolean {
    return this.composing?.kind === kind && this.composing.action === action;
  }

  flagIcon(kind: string): string {
    return WHY_FLAG_ICON[kind] ?? 'fa-flag';
  }

  /**
   * A flag's factors that add to its reason (Plan ITP-P-68 D-P6): every factor except one equal to the reason, trimmed
   * and ignoring case. Empty when none is left, and the panel then shows no factor line.
   */
  extraFactors(flag: AdminDashboardInsightFlag): string[] {
    const reason = (flag.reason ?? '').trim().toLowerCase();
    return (flag.factors ?? []).filter(factor => (factor ?? '').trim().toLowerCase() !== reason);
  }

  scoreTier(score: number): string {
    return whyScoreTier(score);
  }

  /** True when a bar shows 0 (its score rounds to 0): it then draws a 4 px critical nub, not an empty track (Plan ITP-P-68 D-P2). */
  zeroBar(score: number): boolean {
    return Math.round(score ?? 0) === 0;
  }

  /** The open flag's own label for a kind, else the server's name for it, else the kind itself. */
  flagLabel(kind: string): string {
    return this.insight?.flags?.find(f => f.kind === kind)?.label ?? WHY_FLAG_LABEL[kind] ?? kind;
  }

  /** "Snoozed by Ann Admin" / "Marked handled by Ann Admin" / "Reopened by Ann Admin" (OCA-E-159 WhyPanel.tsx:324-326). */
  historyWhat(a: AdminDashboardFlagAction): string {
    const by = a.byName || a.byEmail;
    const what = a.action === 'snoozed' ? 'Snoozed'
      : a.action === 'handled' ? 'Marked handled'
      : a.action === 'reopened' ? 'Reopened'
      : a.action.charAt(0).toUpperCase() + a.action.slice(1);
    return `${what} by ${by}`;
  }

  /** Reopens a snoozed or handled flag (R5 Q9): no note and no confirmation; the flag goes back to the open list. */
  reopen(kind: string): void {
    this.reopening = kind;
    this.act(kind, 'reopen');
  }

  act(kind: string, action: 'snooze' | 'handled' | 'reopen'): void {
    this.busy = true;
    this.error = null;
    const note = action === 'reopen' ? null : this.notes[kind]?.trim() || null;
    this.dataService.flagAction(this.companyId, kind, action, note)
      .pipe(timeout({first: FLAG_ACTION_TIMEOUT_MS}))
      .subscribe({
        next: response => {
          this.busy = false;
          const message = response?.fieldValues?.['ErrorMessage'];
          if (message) {
            this.error = message;
            return;
          }
          this.notes[kind] = '';
          this.composing = null;
          this.reopening = null;
          this.reload();
          this.changed.emit();
        },
        error: err => {
          this.busy = false;
          this.error = err instanceof TimeoutError ? FLAG_ACTION_TIMEOUT_MESSAGE : 'Could not save. Try again.';
        }
      });
  }

  /**
   * The chart container exists only while there is an insight: the chart is created when it appears and disposed
   * when it goes.
   */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = whyPanelCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.commercialSubscription?.unsubscribe();
    this.featureSubscription?.unsubscribe();
    this.disposeChart();
  }

  private reload(): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.dataService.getCompanyInsight(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyInsight))
    ).subscribe(data => {
      this.data = data;
      this.render();
      this.loadLicences();
    });
  }

  /**
   * The Contract block's licences (ITP-E-165-07, SPEC API-3): read through `AdminDashboardCompanyCommercialQuery`
   * (APClientManager) only for a client and only when the viewer holds that feature; the live ones (Active or
   * Upcoming, the licences the ARR row counts) are kept. A failed call reads as no licences.
   */
  private loadLicences(): void {
    this.commercialSubscription?.unsubscribe();
    this.licences = null;
    if (!this.granted('APClientManager') || !this.insight?.isClient) {
      return;
    }
    this.commercialSubscription = this.dataService.getCompanyCommercial(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyCommercial))
    ).subscribe(commercial => {
      this.licences = (commercial?.Licences ?? []).filter(l => l.status === 'Active' || l.status === 'Upcoming');
    });
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  /**
   * The 16-week logins bars (ITP-E-165-04/08) (OCA-E-159 charts.ts:607-640 whyLoginsOption): the last four weeks strong, the earlier
   * ones pale, and the 12-week average as a line. The mockup's renewal threshold line ("60% line") is left out: the
   * panel's data (AdminDashboardCompanyInsightQuery) does not carry the `renewalLoginRatio` setting.
   */
  private render(): void {
    if (!this.chart) {
      return;
    }
    const weekly = this.data?.WeeklyLogins ?? [];
    const labels = weekly.map(p => formatDate(p.date, 'd MMM', 'en-US', 'UTC'));
    const average = this.loginsAverage;
    const t = adbChartTheme(this.chartElement);
    const strongFrom = weekly.length - 4;
    // ITP-E-302-07 (Plan ITP-P-68 D-P7): no label on the average — the note above the chart states it, and a label
    // sat on the last bars.
    const averageLine = {
      silent: true,
      symbol: 'none',
      label: {show: false},
      data: [{yAxis: average, lineStyle: {color: t.ink2, type: 'solid', width: 1}}]
    };
    const option = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid({top: 24}),
      xAxis: adbCategoryAxis(t, labels, {axisLabel: {color: t.ink2, fontSize: 10, interval: 3}}),
      yAxis: adbValueAxis(t, v => formatNumber(v, 'en-US', '1.0-0'), {minInterval: 1, splitNumber: 3}),
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: {dataIndex: number}[]) => this.weekTooltip(labels, weekly, params?.[0]?.dataIndex ?? 0, average, t)
      }),
      series: [{
        type: 'bar',
        barMaxWidth: 14,
        data: weekly.map((p, i) => ({
          value: p.value,
          itemStyle: {color: i >= strongFrom ? t.blue : adbAlpha(t.blue, 0.35), borderRadius: [2, 2, 0, 0]}
        })),
        ...(average >= 1 ? {markLine: averageLine} : {})
      }]
    };
    this.chart.setOption(option as unknown as echarts.EChartsOption, true);
  }

  /** One week's tooltip: its logins and their share of the 12-week average (charts.ts:618-621). */
  private weekTooltip(labels: string[], weekly: AdminDashboardPoint[], index: number, average: number, t: AdbChartTheme): string {
    const value = weekly[index]?.value ?? 0;
    const share = average >= 1
      ? `${formatPercent(value / average, 'en-US', '1.0-0')} of the 12-week average (${formatNumber(average, 'en-US', '1.0-0')})`
      : 'No 12-week average yet';
    return `<b>Week of ${labels[index] ?? ''}</b><br/>${countOf(value, 'login')}<br/><span style="color:${t.ink2}">${share}</span>`;
  }
}
